import React, { useMemo, useState } from 'react';
import { PlayIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { Waveform } from '../../components/archive/AudioTile';
import { Button } from '../../components/ui/Button';
import { ChoiceChip } from '../../components/ui/ChoiceChip';
import { FilterMenu } from '../../components/ui/FilterMenu';
import { TextInput } from '../../components/ui/TextControl';
import { ArchiveTile } from '../../components/archive/ArchiveTile';
import { ArchiveDetail } from '../../components/archive/ArchiveDetail';
import { ARCHIVE_ITEMS, ARCHIVE_KIND_LABELS, decadeOf, itemsInYears, thinYears } from '../../data/archive';
import type { ArchiveItem, ArchiveKind } from '../../data/archive';
import { CURRENT_MEMBER, DECADES, parseYears } from '../../data/community';
const KINDS: ArchiveKind[] = ['photograph', 'audio', 'memory', 'artifact', 'record'];
export function Archive() {
  const [query, setQuery] = useState('');
  const [decade, setDecade] = useState<string | null>(null);
  const [kind, setKind] = useState<ArchiveKind | null>(null);
  const [myYearsOnly, setMyYearsOnly] = useState(false);
  const [open, setOpen] = useState<ArchiveItem | null>(null);
  const you = parseYears(CURRENT_MEMBER.years);
  const yours = itemsInYears(you.start, you.end);
  const thin = thinYears(you.start, you.end);
  const results = useMemo(() => ARCHIVE_ITEMS.filter(item => {
    if (decade && decadeOf(item.year) !== decade) return false;
    if (kind && item.kind !== kind) return false;
    if (myYearsOnly && (item.year < you.start || item.year > you.end)) return false;
    if (query) {
      const haystack = `${item.title} ${item.quote ?? ''} ${item.credit}`.toLowerCase();
      if (!haystack.includes(query.toLowerCase())) return false;
    }
    return true;
  }).sort((a, b) => a.year - b.year), [decade, kind, myYearsOnly, query, you.start, you.end]);
  return <div className="min-h-full bg-card" data-id="element-552" data-mp="31lqynrs1xz1o">
      {/* YOUR YEARS — the archive indexed by who you are, before any filter. */}
      <section aria-labelledby="your-years" className="bg-canvas" data-id="element-553" data-mp="38rtg686spa02">
        <div className="mx-auto w-full max-w-6xl px-4 pb-6 pt-6 sm:px-6" data-id="element-554" data-mp="3dniiuke4370l">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-555" data-mp="3indc2uld08ot">
            <h1 id="your-years" className="text-section-heading font-semibold text-ink" data-id="element-556" data-mp="3nmpyje8bjdqv">
              {CURRENT_MEMBER.years} — the years you were here
            </h1>
            <p className="text-small text-ink-muted" data-id="element-557" data-mp="2px4r6uzfva6j">
              {yours.length} {yours.length === 1 ? 'item' : 'items'} in the archive
            </p>
          </div>

          <ul className="mt-4 flex snap-x gap-3 overflow-x-auto pb-1" data-id="element-558" data-mp="2zvu7ijbosmko">
            {yours.map(item => <li key={item.id} className="w-56 shrink-0 snap-start overflow-hidden rounded-field border border-rule bg-card" data-id="element-559" data-mp="34pgafjs1tc3a">
                <button type="button" onClick={() => setOpen(item)} className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset" data-id="element-560" data-mp="2c6swcobgyxbp">
                  {item.kind === 'audio' ? <span className="flex h-28 flex-col justify-center gap-2 bg-ink px-4" data-id="element-561" data-mp="kagrn8jn7l51">
                      <span className="flex items-center gap-2 text-highlight" data-id="element-562" data-mp="fc5gmuhm34l8">
                        <PlayIcon aria-hidden="true" className="h-4 w-4 shrink-0" data-id="element-563" data-mp="ae41gknb3pf1" />
                        <Waveform seed={item.id} className="h-6 min-w-0 flex-1" data-id="element-564" data-mp="5mlv1h2ikwmo" />
                      </span>
                      <span className="text-small text-on-ink opacity-70" data-id="element-565" data-mp="1kqysqzfx9fr">
                        Recording · {item.duration}
                      </span>
                    </span> : item.image ? <img src={item.image.src} alt={item.image.alt} className="h-28 w-full object-cover" data-id="element-566" data-mp="3nrffzuu49vpb" /> : <span className="flex h-28 items-center px-4 text-body italic text-ink-muted" data-id="element-567" data-mp="1mrat0y7s9v2f">
                      {item.quote ? `“${item.quote.slice(0, 60)}…”` : item.title}
                    </span>}
                  <span className="flex flex-col gap-0.5 p-3" data-id="element-568" data-mp="1hzssgb9o9whz">
                    <span className="text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-569" data-mp="1hy7yonv6mhl6">
                      {item.year} · {ARCHIVE_KIND_LABELS[item.kind]}
                    </span>
                    <span className="line-clamp-2 text-small text-ink" data-id="element-570" data-mp="190p873yrrb1w">{item.title}</span>
                  </span>
                </button>
              </li>)}
          </ul>

          {thin.length > 0 ? <div className="mt-4 flex flex-col gap-3 rounded-field border border-rule bg-card p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-571" data-mp="z310ldg2e92b">
              <p className="text-small text-ink-muted" data-id="element-572" data-mp="2tfhcatrmqfek">
                <span className="font-medium text-ink" data-id="element-573" data-mp="2tcv6ayke07yg">
                  {thin.map(entry => entry.year).join(', ')}
                </span>{' '}
                {thin.length === 1 ? 'has' : 'have'} almost nothing in{' '}
                {thin.length === 1 ? 'it' : 'them'}. You were here — whatever you add
                won&apos;t be one account among many.
              </p>
              <Button className="shrink-0" data-id="element-574" data-mp="mp48y335ec74">
                <PlusIcon aria-hidden="true" className="h-4 w-4" data-id="element-575" data-mp="10hu0en1g49bm" />
                Add something
              </Button>
            </div> : null}
        </div>
      </section>

      {/* EVERYTHING ELSE */}
      <div className="border-b border-rule bg-canvas" data-id="element-576" data-mp="1agt7402nhuvz">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 pb-6 sm:px-6" data-id="element-577" data-mp="1ae71i5zblpge">
          <div className="flex flex-wrap items-baseline gap-x-3" data-id="element-578" data-mp="15hzsu5fjhzur">
            <h2 className="text-heading font-semibold text-ink" data-id="element-579" data-mp="3c8pin9otj4qr">Fifty years</h2>
            <p className="text-small text-ink-muted" data-id="element-580" data-mp="37935t1cwjqih">
              {results.length} of {ARCHIVE_ITEMS.length} shown
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-id="element-581" data-mp="1b79i5lj8mhzk">
            <label className="relative flex min-w-44 max-w-xs flex-1 items-center" data-id="element-582" data-mp="1g3qayt1dtkiz">
              <SearchIcon aria-hidden="true" className="pointer-events-none absolute left-3.5 h-4 w-4 text-ink-muted" data-id="element-583" data-mp="1k5liif9wou57" />
              <span className="sr-only" data-id="element-584" data-mp="1p2ts3rgznpuu">Search the archive</span>
              <TextInput type="search" placeholder="Search the archive" className="pl-10" value={query} onChange={event => setQuery(event.target.value)} data-id="element-585" data-mp="1u3hpqvzq34y1" />
            </label>

            <ChoiceChip checked={myYearsOnly} onChange={() => setMyYearsOnly(!myYearsOnly)} className="shrink-0" data-id="element-586" data-mp="27vxo3jhoiesm">
              My years
            </ChoiceChip>

            <ChoiceChip checked={kind === 'memory'} onChange={() => setKind(kind === 'memory' ? null : 'memory')} className="shrink-0" data-id="element-587" data-mp="2huwvyzume6ek">
              Memories
            </ChoiceChip>

            <span aria-hidden="true" className="h-6 w-px shrink-0 bg-rule" data-id="element-588" data-mp="2n124bp5a1p3b" />

            <div className="shrink-0" data-id="element-589" data-mp="2i04k770pikz6">
            <FilterMenu label="Years" allLabel="All" value={decade} onChange={setDecade} options={[{
              label: 'All years',
              value: null
            }, ...DECADES.map(item => ({
              label: item,
              value: item
            }))]} data-id="element-590" data-mp="2dy10xw7sqc3c" />
            </div>

            <div className="shrink-0" data-id="element-591" data-mp="30x5sp1tgz37v">
            <FilterMenu label="Kind" allLabel="Everything" value={kind} onChange={next => setKind(next as ArchiveKind | null)} options={[{
              label: 'Everything',
              value: null
            }, ...KINDS.map(item => ({
              label: ARCHIVE_KIND_LABELS[item],
              value: item
            }))]} data-id="element-592" data-mp="30w4743etuybw" />
            </div>

            <Button variant="secondary" className="ml-auto shrink-0" data-id="element-593" data-mp="gxflp13hd7eq">
              <PlusIcon aria-hidden="true" className="h-4 w-4" data-id="element-594" data-mp="gyqp2zt0kd5b" />
              Add to the archive
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6" data-id="element-595" data-mp="76tdhry3fj62">
        {results.length > 0 ? <ul className="columns-1 gap-4 sm:columns-2 lg:columns-3 [&>li]:mb-4" data-id="element-596" data-mp="uwxavhkoumom">
            {results.map(item => <ArchiveTile key={item.id} item={item} onOpen={setOpen} data-id="element-597" data-mp="zvruglqhcf9s" />)}
          </ul> : <div className="rounded-card border border-dashed border-rule p-10 text-center" data-id="element-598" data-mp="kyrfzjxaugt9">
            <p className="text-body text-ink" data-id="element-599" data-mp="pwj74bq4dywe">Nothing here matches that.</p>
            <p className="mx-auto mt-2 max-w-sm text-small text-ink-muted" data-id="element-600" data-mp="35o6xfoamot68">
              Thin years are the norm rather than the exception — most of this archive is what
              people brought back.
            </p>
          </div>}

        <p className="mt-8 border-t border-rule pt-6 text-small text-ink-muted" data-id="element-601" data-mp="1ssj9oyzenc6y">
          That&apos;s the whole archive — {ARCHIVE_ITEMS.length} items so far, and it only grows
          when someone adds to it.
        </p>
      </div>

      <ArchiveDetail item={open} onClose={() => setOpen(null)} data-id="element-602" data-mp="1xpjdt4cp24vf" />
    </div>;
}